/**
 * BirthdaySurpriseModal — full-screen modal that appears on the kid's
 * birthday (per their stored birthday_year) with a one-tap claim flow for
 * the bonus coins, extra play minutes, and collectible badge.
 *
 * 调用关系：
 *   - Mounted by App.tsx whenever ctx.activePet is set + birthday window
 *   - api.birthdaySurprise() preview + api.claimBirthdaySurprise() →
 *     backend/src/routes/birthday.js
 *   - Fires a coin burst via fireBurst() so the wallet animates
 *
 * 关键约束：
 *   - Per-year dismiss: the dismissedKey is `${childId}:${birthday_year}`
 *     so next year the modal pops up again automatically.
 *   - claimed state is local-only; backend is source of truth, this just
 *     suppresses re-render until the next refresh.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { BirthdaySurprisePreview } from '@xiaopeiban/shared';
import { api, ApiCallError } from '../api/client';
import { useLang } from '../i18n';
import { Coins } from './ui/Coins';
import { fireBurst } from './RewardBurst';

interface Props {
  childId: number;
  enabled: boolean;
  onClaimed: () => void | Promise<void>;
}

export default function BirthdaySurpriseModal({ childId, enabled, onClaimed }: Props) {
  const lang = useLang();
  const [surprise, setSurprise] = useState<BirthdaySurprisePreview | null>(null);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimedHere, setClaimedHere] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api.birthdaySurprise()
      .then((r) => {
        if (!cancelled) setSurprise(r.birthday_surprise);
      })
      .catch(() => { /* birthday preview is optional; keep boot quiet */ });
    return () => { cancelled = true; };
  }, [childId, enabled]);

  const key = useMemo(() => {
    if (!surprise?.birthday_year) return null;
    return `${childId}:${surprise.birthday_year}`;
  }, [childId, surprise?.birthday_year]);

  if (!enabled || !surprise?.eligible || !surprise.is_birthday_today || !surprise.rewards) return null;
  if (surprise.claimed && !claimedHere) return null;
  if (key && dismissedKey === key) return null;

  const title = lang === 'en' ? surprise.title_en : surprise.title_zh;
  const message = lang === 'en' ? surprise.message_en : surprise.message_zh;
  const badge = surprise.rewards.badge;
  const badgeTitle = lang === 'en' ? badge.title_en : badge.title_zh;
  const badgeDesc = lang === 'en' ? badge.description_en : badge.description_zh;
  const claimed = surprise.claimed || claimedHere;

  async function claim() {
    setClaiming(true);
    setErr(null);
    try {
      const r = await api.claimBirthdaySurprise();
      setSurprise(r.birthday_surprise);
      setClaimedHere(true);
      const points = r.birthday_surprise.rewards?.points || 0;
      if (points > 0) fireBurst({ kind: 'coin', amount: points });
      void Promise.resolve(onClaimed()).catch(() => {});
    } catch (e) {
      setErr(e instanceof ApiCallError ? e.message : (lang === 'en' ? 'Could not open this surprise.' : '暂时打不开这个惊喜。'));
    } finally {
      setClaiming(false);
    }
  }

  function close() {
    if (key) setDismissedKey(key);
  }

  return (
    <div role="dialog" aria-modal="true" style={backdropStyle}>
      <div style={cardStyle}>
        <div style={sparkleStyle} aria-hidden>✦</div>
        <div style={iconStyle} aria-hidden>🎂</div>
        <h2 style={titleStyle}>{claimed ? (lang === 'en' ? 'Surprise opened' : '惊喜已打开') : title}</h2>
        <p style={bodyStyle}>{message}</p>

        <div style={rewardGridStyle}>
          <RewardTile
            label={lang === 'en' ? 'Birthday coins' : '生日金币'}
            value={<Coins sen={surprise.rewards.points} size="lg" />}
          />
          <RewardTile
            label={lang === 'en' ? 'Extra play today' : '今天额外玩耍'}
            value={lang === 'en'
              ? `+${surprise.rewards.extra_play_minutes} min`
              : `+${surprise.rewards.extra_play_minutes} 分钟`}
          />
          <RewardTile
            label={lang === 'en' ? 'Collectible badge' : '收藏徽章'}
            value={`${badge.icon} ${badgeTitle}`}
            hint={badgeDesc}
          />
        </div>

        {err && <div style={errorStyle}>{err}</div>}

        <div style={{
          ...actionsStyle,
          gridTemplateColumns: claimed ? '1fr' : '1fr 1.4fr',
        }}>
          {!claimed && (
            <button type="button" style={secondaryButtonStyle} disabled={claiming} onClick={close}>
              {lang === 'en' ? 'Later' : '稍后'}
            </button>
          )}
          <button
            type="button"
            style={primaryButtonStyle}
            disabled={claiming}
            onClick={claimed ? close : () => void claim()}
          >
            {claiming
              ? <span className="spinner" />
              : claimed
                ? (lang === 'en' ? 'Done' : '收下啦')
                : (lang === 'en' ? 'Open surprise' : '打开惊喜')}
          </button>
        </div>
      </div>
    </div>
  );
}

function RewardTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div style={tileStyle}>
      <div style={tileLabelStyle}>{label}</div>
      <div style={tileValueStyle}>{value}</div>
      {hint && <div style={tileHintStyle}>{hint}</div>}
    </div>
  );
}

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 9250,
  minHeight: '100dvh',
  display: 'grid',
  placeItems: 'center',
  padding: 'max(18px, env(safe-area-inset-top)) 18px max(18px, env(safe-area-inset-bottom))',
  background: 'rgba(45, 22, 34, 0.62)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};

const cardStyle: CSSProperties = {
  position: 'relative',
  width: 'min(92vw, 520px)',
  maxHeight: 'min(86dvh, 720px)',
  overflow: 'auto',
  borderRadius: 28,
  padding: '28px 22px 22px',
  background: 'linear-gradient(180deg, #fff7ed 0%, #fff1f2 52%, #fef3c7 100%)',
  color: '#7c2d12',
  textAlign: 'center',
  boxShadow: '0 28px 80px rgba(0, 0, 0, 0.38), inset 0 0 0 2px rgba(255, 255, 255, 0.75)',
};

const sparkleStyle: CSSProperties = {
  position: 'absolute',
  top: 16,
  right: 22,
  color: '#f59e0b',
  fontSize: 28,
  fontWeight: 900,
};

const iconStyle: CSSProperties = {
  width: 76,
  height: 76,
  margin: '0 auto 10px',
  borderRadius: 24,
  display: 'grid',
  placeItems: 'center',
  background: '#fff',
  fontSize: 42,
  boxShadow: '0 14px 34px rgba(217, 119, 6, 0.2)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 25,
  lineHeight: 1.18,
  fontWeight: 900,
  letterSpacing: 0,
};

const bodyStyle: CSSProperties = {
  margin: '10px auto 18px',
  maxWidth: 430,
  fontSize: 15,
  lineHeight: 1.65,
  fontWeight: 650,
};

const rewardGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))',
  gap: 10,
};

const tileStyle: CSSProperties = {
  minHeight: 120,
  borderRadius: 18,
  padding: '13px 10px',
  background: 'rgba(255, 255, 255, 0.78)',
  border: '1px solid rgba(251, 191, 36, 0.38)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 6,
};

const tileLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  color: '#a16207',
};

const tileValueStyle: CSSProperties = {
  fontSize: 15,
  lineHeight: 1.25,
  fontWeight: 900,
  color: '#7c2d12',
};

const tileHintStyle: CSSProperties = {
  fontSize: 11,
  lineHeight: 1.35,
  color: '#9a3412',
};

const actionsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1.4fr',
  gap: 10,
  marginTop: 18,
};

const primaryButtonStyle: CSSProperties = {
  minHeight: 48,
  borderRadius: 16,
  padding: '12px 16px',
  background: 'linear-gradient(135deg, #f97316, #db2777)',
  color: '#fff',
  fontSize: 16,
  fontWeight: 900,
  boxShadow: '0 10px 22px rgba(219, 39, 119, 0.28)',
};

const secondaryButtonStyle: CSSProperties = {
  minHeight: 48,
  borderRadius: 16,
  padding: '12px 16px',
  background: 'rgba(255, 255, 255, 0.72)',
  color: '#92400e',
  fontSize: 15,
  fontWeight: 850,
  border: '1px solid rgba(251, 191, 36, 0.45)',
};

const errorStyle: CSSProperties = {
  marginTop: 12,
  color: '#991b1b',
  fontSize: 13,
  fontWeight: 700,
};
